import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DeUiIconComponent } from './ui-icon.component.js';

@Component({
  selector: 'de-empty-state',
  standalone: true,
  imports: [DeUiIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="de-state">
      <span aria-hidden="true"><de-ui-icon name="empty" /></span>
      <span class="de-state-title">{{
        isSearching() ? 'No matching documents' : 'This folder is empty'
      }}</span>
      <span>{{
        isSearching() ? 'Nothing here matches “' + query() + '”.' : 'There is nothing to show here yet.'
      }}</span>
    </div>
  `,
})
export class DeEmptyStateComponent {
  readonly isSearching = input<boolean>(false);
  readonly query = input<string>('');
}

@Component({
  selector: 'de-loading-state',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div aria-busy="true" aria-live="polite" aria-label="Loading documents">
      @for (width of widths(); track $index) {
        <div class="de-skeleton-row">
          <span class="de-skeleton-bar" [style.width.%]="width"></span>
        </div>
      }
    </div>
  `,
})
export class DeLoadingStateComponent {
  readonly rows = input<number>(5);

  // Varying widths read as content loading rather than a progress bar.
  protected readonly widths = computed(() =>
    Array.from({ length: this.rows() }, (_, i) => 40 + ((i * 17) % 45)),
  );
}

@Component({
  selector: 'de-error-state',
  standalone: true,
  imports: [DeUiIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="de-state de-state--error" role="alert">
      <span aria-hidden="true"><de-ui-icon name="alert" /></span>
      <span class="de-state-title">Could not load documents</span>
      <span>{{ message() }}</span>
    </div>
  `,
})
export class DeErrorStateComponent {
  readonly error = input<unknown>();

  protected readonly message = computed(() => {
    const error = this.error();
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    return 'Something went wrong.';
  });
}
